import reportService from "@/services/report";
import { transformMealsForPackages, transformMealsForRestaurantCompanies } from "@/mappers/reportMapper";
import { SHIFTS } from "@/constants/shifts";
import { packingStorageKey, readPackingState, summarizePacking } from "../shared/packingStorage";

const DEBOUNCE_MS = 300;

/**
 * Značke u meniju (Obroci: ukupno, Pakovanje: spremne firme / firme, Preuzeto: preuzeto / poručeno).
 * Ljuska ih računa sama iz dva poziva (/obrok i /total) za izabrani period, firme i smene, da bi
 * brojevi bili vidljivi i dok je otvoren drugi izveštaj. Zahteva: from, to, shifts, selectedCompanies,
 * reloadKey i selectedView (samo prikaz 1 ima značke).
 */
export default {
  data: () => ({
    badgeData: { meals: null, packing: null, taken: null },
    packingRevision: 0,
    badgeRequest: 0,
  }),
  computed: {
    /** { [ključ izveštaja]: { text, complete } } */
    badges() {
      const { meals, taken } = this.badgeData;
      const badges = {};
      if (meals !== null) {
        badges.meals = { text: String(meals) };
      }
      const packing = this.packingBadge;
      if (packing) {
        badges.packages = packing;
      }
      if (taken) {
        badges.taken = { text: `${taken.taken}/${taken.ordered}`, complete: taken.ordered > 0 && taken.taken >= taken.ordered };
      }
      return badges;
    },
    packingBadge() {
      // packingRevision: čeklista se menja u localStorage-u, pa se značka računa iznova
      const raw = this.packingRevision >= 0 ? this.badgeData.packing : null;
      if (!raw) {
        return null;
      }
      const restaurantId = this.$store.state.restaurant?.id;
      const state = readPackingState(packingStorageKey(restaurantId, this.from, this.to));
      const summary = summarizePacking(raw, state, this.shifts);
      if (!summary.companies) {
        return null;
      }
      return {
        text: `${summary.ready}/${summary.companies}`,
        complete: summary.ready === summary.companies,
      };
    },
    badgeKey() {
      return [
        this.selectedView,
        this.from,
        this.to,
        this.shifts.join(""),
        this.selectedCompanies.map((company) => company.id).join(","),
        this.reloadKey,
        this.$store.state.restaurant?.id,
      ].join("|");
    },
  },
  watch: {
    badgeKey: {
      handler() {
        clearTimeout(this.badgeTimer);
        this.badgeTimer = setTimeout(() => this.loadBadges(), DEBOUNCE_MS);
      },
      immediate: true,
    },
  },
  created() {
    this.$root.$on("report-packing-changed", this.onPackingChanged);
  },
  beforeDestroy() {
    clearTimeout(this.badgeTimer);
    this.$root.$off("report-packing-changed", this.onPackingChanged);
  },
  methods: {
    onPackingChanged() {
      this.packingRevision += 1;
    },
    async loadBadges() {
      const restaurantId = this.$store.state.restaurant?.id;
      if (!restaurantId || this.selectedView !== 1) {
        this.badgeData = { meals: null, packing: null, taken: null };
        return;
      }
      const request = ++this.badgeRequest;
      const params = { date: this.from, date2: this.to };
      const companyIds = this.selectedCompanies.map((company) => company.id).join(",");
      const [mealsResult, takenResult] = await Promise.allSettled([
        companyIds
          ? reportService.getMealsRaw(restaurantId, { ...params, company: companyIds })
          : Promise.resolve({ data: null }),
        reportService.getTotalShiftMeals(restaurantId, params),
      ]);
      if (request !== this.badgeRequest) {
        return;
      }
      const next = { meals: null, packing: null, taken: null };
      if (mealsResult.status === "fulfilled" && mealsResult.value.data) {
        try {
          const data = mealsResult.value.data;
          next.meals = this.sumMeals(transformMealsForRestaurantCompanies(data));
          next.packing = transformMealsForPackages(data);
        } catch (error) {
          // Neočekivan oblik odgovora: značke Obroci i Pakovanje se ne prikazuju.
          next.meals = null;
          next.packing = null;
        }
      }
      if (takenResult.status === "fulfilled") {
        next.taken = this.sumTaken(takenResult.value.data);
      }
      this.badgeData = next;
    },
    sumMeals(companies) {
      return companies.reduce(
        (sum, company) =>
          sum +
          company.meals.reduce(
            (mealSum, meal) =>
              mealSum +
              SHIFTS.filter((shift) => this.shifts.includes(shift.id)).reduce(
                (shiftSum, shift) => shiftSum + (meal[shift.key]?.orders || 0),
                0
              ),
            0
          ),
        0
      );
    },
    /** "3/22" po smeni → zbir preuzeto / poručeno za izabrane smene */
    sumTaken(rows) {
      const total = { taken: 0, ordered: 0 };
      (Array.isArray(rows) ? rows : []).forEach((row) => {
        SHIFTS.filter((shift) => this.shifts.includes(shift.id)).forEach((shift) => {
          const [taken, ordered] = String(row[shift.key] || "0/0").split("/");
          total.taken += parseInt(taken) || 0;
          total.ordered += parseInt(ordered) || 0;
        });
      });
      return total;
    },
  },
};
